import { ComponentPropsWithoutRef, ElementType, memo } from 'react'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../style/colorPalette'

// Default color
const DEFAULT_COLOR = colorPalette.monotone[10]

// Style definitions based on Figma design system
const titleStyles = {
  '2l': { fontSize: 20, fontWeight: 500, lineHeight: 1 }, // Barely used. Only when there is large space.
  l: { fontSize: 16, fontWeight: 700, lineHeight: 1 }, // Default.
  m: { fontSize: 15, fontWeight: 500, lineHeight: 1 },
  mBold: { fontSize: 15, fontWeight: 700, lineHeight: 1 },
  s: { fontSize: 14, fontWeight: 700, lineHeight: 1 }, // Frequently used.
} as const

const textStyles = {
  '3l': { fontSize: 20, fontWeight: 700, lineHeight: 1 }, // Barely used. Only when there is large space.
  '2l': { fontSize: 18, fontWeight: 400, lineHeight: 1 }, // Barely used. Only when there is large space.
  l: { fontSize: 16, fontWeight: 500, lineHeight: 1 }, // Barely used. Only when there is large space.
  m: { fontSize: 14, fontWeight: 400, lineHeight: 1 }, // Default.
  mMedium: { fontSize: 14, fontWeight: 500, lineHeight: 1 },
  s: { fontSize: 13, fontWeight: 400, lineHeight: 1 },
  sBold: { fontSize: 13, fontWeight: 700, lineHeight: 1 },
  '2s': { fontSize: 12, fontWeight: 400, lineHeight: 1 },
  '2sMedium': { fontSize: 12, fontWeight: 500, lineHeight: 1 },
  '2sBold': { fontSize: 12, fontWeight: 700, lineHeight: 1 },
  '3s': { fontSize: 10, fontWeight: 400, lineHeight: 1 }, // Barely used. Only when the content is not necessary to be read.
  '3sBold': { fontSize: 10, fontWeight: 700, lineHeight: 1 }, // Barely used. Only when the content is not necessary to be read.
} as const

const multilineTextStyles = {
  m: { fontSize: 14, fontWeight: 400, lineHeight: 1.5 },
  mMedium: { fontSize: 14, fontWeight: 500, lineHeight: 1.5 },
  s: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
  '2s': { fontSize: 12, fontWeight: 400, lineHeight: 1.5 }, // Default.
  '2sMedium': { fontSize: 12, fontWeight: 500, lineHeight: 1.5 }, // Barely used. Only when the content is not necessary to be read.
  '3s': { fontSize: 10, fontWeight: 400, lineHeight: 1.5 }, // Barely used. Only when the content is not necessary to be read.
} as const

// Type definitions
type TitleVariant = keyof typeof titleStyles
type TextVariant = keyof typeof textStyles
type MultilineTextVariant = keyof typeof multilineTextStyles

type PolymorphicProps<T extends ElementType, V> = {
  as?: T
  variant?: V
  color?: string
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'variant' | 'color'>

// Styled components
const StyledTitle = styled('span')<{ variant: TitleVariant; color: string }>(
  ({ variant, color }) => ({
    ...titleStyles[variant],
    color,
  })
)

const StyledText = styled('span')<{ variant: TextVariant; color: string }>(
  ({ variant, color }) => ({
    ...textStyles[variant],
    color,
  })
)

const StyledMultilineText = styled('p')<{
  variant: MultilineTextVariant
  color: string
}>(({ variant, color }) => ({
  ...multilineTextStyles[variant],
  color,
  margin: 0,
}))

// Title component - for headings (default: span, variant: 'l', lineHeight: 1)
const TitleComponent = <T extends ElementType = 'span'>({
  as,
  variant = 'l',
  color = DEFAULT_COLOR,
  ...props
}: PolymorphicProps<T, TitleVariant>) => {
  return <StyledTitle as={as} variant={variant} color={color} {...props} />
}

export const Title = memo(TitleComponent) as typeof TitleComponent & {
  displayName?: string
}
Title.displayName = 'Title'

// Text component - for single-line text (default: span, variant: 'm', lineHeight: 1)
const TextComponent = <T extends ElementType = 'span'>({
  as,
  variant = 'm',
  color = DEFAULT_COLOR,
  ...props
}: PolymorphicProps<T, TextVariant>) => {
  return <StyledText as={as} variant={variant} color={color} {...props} />
}

export const Text = memo(TextComponent) as typeof TextComponent & {
  displayName?: string
}
Text.displayName = 'Text'

// MultilineText component - for multi-line text (default: p, variant: '2s', lineHeight: 1.5, no Bold variants)
const MultilineTextComponent = <T extends ElementType = 'p'>({
  as,
  variant = '2s',
  color = DEFAULT_COLOR,
  ...props
}: PolymorphicProps<T, MultilineTextVariant>) => {
  return (
    <StyledMultilineText as={as} variant={variant} color={color} {...props} />
  )
}

export const MultilineText = memo(
  MultilineTextComponent
) as typeof MultilineTextComponent & {
  displayName?: string
}
MultilineText.displayName = 'MultilineText'
